import { useMemo, useState } from 'react';
import { PlatformApiError } from '../api/client.ts';
import { useUsageByKey, useUsageDaily, useUsageHourly, useUsageSummary } from '../api/useUsage.ts';
import { DailyUsageChart } from '../components/DailyUsageChart.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { TechnicalDetails } from '../components/TechnicalDetails.tsx';
import { UsageChart } from '../components/UsageChart.tsx';
import { formatTokens, formatUsd } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Usage page — daily stacked chart + hourly chart + by-key breakdown.
 *
 * Time range is selected via segmented controls:
 *   - The single page-level picker drives `useUsageDaily`,
 *     `useUsageHourly`, `useUsageSummary`, and `useUsageByKey`, so every
 *     card tells the same time-window story.
 *
 * Both charts are hand-rolled SVGs (R4 non-negotiable: no chart library).
 * Empty + error states are surfaced inline in each card.
 */

type RangeOption = {
  readonly id: string;
  readonly labelKey: I18nKey;
  readonly days: number;
};

const RANGE_OPTIONS: readonly RangeOption[] = [
  { id: '24h', labelKey: 'usage.range.24h', days: 1 },
  { id: '7d', labelKey: 'usage.range.7d', days: 7 },
  { id: '30d', labelKey: 'usage.range.30d', days: 30 },
];

function rangeFromDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function Usage(): JSX.Element {
  const t = useT();
  const [selected, setSelected] = useState<RangeOption>(
    RANGE_OPTIONS[1] ?? RANGE_OPTIONS[0] ?? { id: '7d', labelKey: 'usage.range.7d', days: 7 },
  );

  const range = useMemo(() => rangeFromDays(selected.days), [selected]);
  const summaryQ = useUsageSummary(range);
  const hourlyQ = useUsageHourly(selected.days);
  const dailyQ = useUsageDaily(selected.days);
  const byKeyQ = useUsageByKey(range);

  const summary = summaryQ.data;
  const selectedLabel = t(selected.labelKey);

  return (
    <section data-testid="usage-page" className="flex flex-col gap-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
            {t('usage.title')}
          </h2>
          <p className="text-sm text-outline">{t('usage.subtitle')}</p>
        </div>
        <div
          role="radiogroup"
          aria-label={t('usage.range.aria_label')}
          data-testid="usage-range-picker"
          className="inline-flex items-center gap-1 rounded-lg bg-surface-container-low p-1"
        >
          {RANGE_OPTIONS.map((opt) => {
            const isActive = opt.id === selected.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => setSelected(opt)}
                data-testid={`usage-range-${opt.id}`}
                className={[
                  'inline-flex items-center justify-center rounded-lg px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors',
                  isActive
                    ? 'bg-primary-container text-on-primary'
                    : 'bg-transparent text-outline hover:text-on-surface',
                ].join(' ')}
              >
                {t(opt.labelKey)}
              </button>
            );
          })}
        </div>
      </header>

      <article
        data-testid="usage-daily-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <header className="flex flex-col gap-1">
          <div className="flex flex-col gap-1">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
              {t('usage.daily.title', { label: selectedLabel })}
            </h3>
            <p className="text-xs text-outline">{t('usage.daily.subtitle')}</p>
          </div>
        </header>
        {dailyQ.isLoading ? (
          <div data-testid="usage-daily-loading">
            <Skeleton className="h-48" />
            <span className="sr-only">{t('usage.daily.loading')}</span>
          </div>
        ) : dailyQ.isError ? (
          <p className="text-sm text-error" role="alert" data-testid="usage-daily-error">
            {dailyQ.error instanceof PlatformApiError
              ? `${dailyQ.error.code}: ${dailyQ.error.message}`
              : t('usage.daily.error')}
          </p>
        ) : dailyQ.data !== undefined && dailyQ.data.days.length === 0 ? (
          <p className="text-sm text-outline" data-testid="usage-daily-empty">
            {t('usage.daily.empty', { label: selectedLabel })}
          </p>
        ) : (
          <>
            <DailyUsageChart buckets={dailyQ.data?.days ?? []} testId="usage-daily-chart" />
            <div className="flex items-center gap-4 text-xs text-outline">
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-lg bg-primary-container"
                />
                {t('usage.legend.input')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-lg bg-tertiary"
                />
                {t('usage.legend.output')}
              </span>
            </div>
          </>
        )}
      </article>

      <div
        data-testid="usage-summary-grid"
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
      >
        <SummaryCard
          label={t('usage.summary.requests')}
          value={summary !== undefined ? formatTokens(summary.totalRequests) : '—'}
          hint={
            summary !== undefined
              ? t('usage.summary.range', { label: selectedLabel })
              : t('usage.summary.loading')
          }
          testId="usage-summary-requests"
        />
        <SummaryCard
          label={t('usage.summary.input_tokens')}
          value={summary !== undefined ? formatTokens(summary.totalInputTokens) : '—'}
          hint={
            summary !== undefined
              ? t('usage.summary.range', { label: selectedLabel })
              : t('usage.summary.loading')
          }
          testId="usage-summary-input-tokens"
        />
        <SummaryCard
          label={t('usage.summary.output_tokens')}
          value={summary !== undefined ? formatTokens(summary.totalOutputTokens) : '—'}
          hint={
            summary !== undefined
              ? t('usage.summary.range', { label: selectedLabel })
              : t('usage.summary.loading')
          }
          testId="usage-summary-output-tokens"
        />
        <SummaryCard
          label={t('usage.summary.cost')}
          value={summary !== undefined ? formatUsd(summary.totalCostUsdCents) : '—'}
          hint={summary !== undefined ? t('usage.summary.cost_unit') : t('usage.summary.loading')}
          testId="usage-summary-cost"
        />
      </div>

      {summary !== undefined ? (
        <TechnicalDetails summary={t('usage.technical.summary')} testId="usage-technical-card">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
                {t('usage.technical.cache_read')}
              </dt>
              <dd className="font-mono text-on-surface-variant">
                {formatTokens(summary.totalCacheReadTokens)}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
                {t('usage.technical.cache_write')}
              </dt>
              <dd className="font-mono text-on-surface-variant">
                {formatTokens(summary.totalCacheWriteTokens)}
              </dd>
            </div>
          </dl>
        </TechnicalDetails>
      ) : null}

      <article
        data-testid="usage-chart-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <header className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('usage.chart.title', { label: selectedLabel })}
          </h3>
        </header>
        {hourlyQ.isLoading ? (
          <div data-testid="usage-chart-loading">
            <Skeleton className="h-48" />
            <span className="sr-only">{t('usage.chart.loading')}</span>
          </div>
        ) : hourlyQ.isError ? (
          <p className="text-sm text-error" role="alert" data-testid="usage-chart-error">
            {hourlyQ.error instanceof PlatformApiError
              ? `${hourlyQ.error.code}: ${hourlyQ.error.message}`
              : t('usage.chart.error')}
          </p>
        ) : (
          <UsageChart buckets={hourlyQ.data?.buckets ?? []} testId="usage-chart" />
        )}
      </article>

      <article
        data-testid="usage-by-key-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <header className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('usage.by_key.title', { label: selectedLabel })}
          </h3>
        </header>
        {byKeyQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="usage-by-key-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
            <span className="sr-only">{t('usage.by_key.loading')}</span>
          </div>
        ) : byKeyQ.isError ? (
          <p className="text-sm text-error" role="alert" data-testid="usage-by-key-error">
            {byKeyQ.error instanceof PlatformApiError
              ? `${byKeyQ.error.code}: ${byKeyQ.error.message}`
              : t('usage.by_key.error')}
          </p>
        ) : byKeyQ.data !== undefined && byKeyQ.data.keys.length === 0 ? (
          <p className="text-sm text-outline" data-testid="usage-by-key-empty">
            {t('usage.by_key.empty')}
          </p>
        ) : byKeyQ.data !== undefined ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="usage-by-key-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
                  <th className="px-3 py-2 font-semibold">{t('usage.by_key.column.key_name')}</th>
                  <th className="px-3 py-2 font-semibold">{t('usage.by_key.column.requests')}</th>
                  <th className="px-3 py-2 font-semibold">
                    {t('usage.by_key.column.input_tokens')}
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {t('usage.by_key.column.output_tokens')}
                  </th>
                  <th className="px-3 py-2 font-semibold">{t('usage.by_key.column.cache_read')}</th>
                  <th className="px-3 py-2 font-semibold">
                    {t('usage.by_key.column.cache_write')}
                  </th>
                  <th className="px-3 py-2 font-semibold">{t('usage.by_key.column.cost')}</th>
                </tr>
              </thead>
              <tbody>
                {[...byKeyQ.data.keys]
                  .sort((a, b) => b.requests - a.requests)
                  .map((k) => (
                    <tr
                      key={k.apiKeyId}
                      className="odd:bg-surface-container-low"
                      data-testid={`usage-by-key-row-${k.apiKeyId}`}
                    >
                      <td className="px-3 py-2 font-semibold text-on-surface">
                        {keyDisplayName(k.apiKeyName, k.apiKeyPrefix, t)}
                      </td>
                      <td className="px-3 py-2 font-mono text-on-surface">
                        {formatTokens(k.requests)}
                      </td>
                      <td className="px-3 py-2 font-mono text-on-surface-variant">
                        {formatTokens(k.inputTokens)}
                      </td>
                      <td className="px-3 py-2 font-mono text-on-surface-variant">
                        {formatTokens(k.outputTokens)}
                      </td>
                      <td className="px-3 py-2 font-mono text-on-surface-variant">
                        {formatTokens(k.cacheReadTokens)}
                      </td>
                      <td className="px-3 py-2 font-mono text-on-surface-variant">
                        {formatTokens(k.cacheWriteTokens)}
                      </td>
                      <td className="px-3 py-2 font-mono text-primary">
                        {formatUsd(k.costUsdCents)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </article>
    </section>
  );
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return t('usage.by_key.unnamed_key');
  return t('usage.by_key.unknown_key');
}

interface SummaryCardProps {
  readonly label: string;
  readonly value: string;
  readonly hint: string;
  readonly testId: string;
}

function SummaryCard({ label, value, hint, testId }: SummaryCardProps): JSX.Element {
  return (
    <article
      data-testid={testId}
      className="flex flex-col gap-3 rounded-xl bg-surface-container p-6"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label}
      </span>
      <span className="text-4xl font-semibold tracking-tight text-primary">{value}</span>
      <span className="text-xs text-outline">{hint}</span>
    </article>
  );
}
